import { useCallback, useState } from 'react';

export type BulkPrivacyChange = (imageIds: number[], isPublic: boolean) => void;

export interface UseGalleryManageModeReturn {
  isManaging: boolean;
  selectedIds: Set<number>;
  enterManageMode: () => void;
  cancel: () => void;
  toggleSelected: (imageId: number) => void;
  setSelectedPrivacy: (isPublic: boolean) => void;
}

/** Selection state for the grid's manage mode. The host shows and saves the change. */
const useGalleryManageMode = (
  onBulkPrivacyChange: BulkPrivacyChange | undefined
): UseGalleryManageModeReturn => {
  const [isManaging, setIsManaging] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());

  const enterManageMode = useCallback((): void => {
    setIsManaging(true);
    setSelectedIds(new Set());
  }, []);

  const cancel = useCallback((): void => {
    setIsManaging(false);
    setSelectedIds(new Set());
  }, []);

  const toggleSelected = useCallback((imageId: number): void => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(imageId)) {
        next.delete(imageId);
      } else {
        next.add(imageId);
      }
      return next;
    });
  }, []);

  const setSelectedPrivacy = useCallback(
    (isPublic: boolean): void => {
      if (!onBulkPrivacyChange || selectedIds.size === 0) return;

      onBulkPrivacyChange([...selectedIds], isPublic);
      cancel();
    },
    [onBulkPrivacyChange, selectedIds, cancel]
  );

  return {
    isManaging,
    selectedIds,
    enterManageMode,
    cancel,
    toggleSelected,
    setSelectedPrivacy,
  };
};

export default useGalleryManageMode;
